import { useState, useCallback, memo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faInfoCircle,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useWidgetSettings } from "../WidgetSettings";

// Helper function to validate Calendly URLs
const validateCalendlyUrl = (url) => {
  if (!url) return { isValid: false, error: "" };

  try {
    const urlObj = new URL(url);

    // Check if it's a Calendly domain
    if (urlObj.hostname !== "calendly.com") {
      return {
        isValid: false,
        error: "URL must be from calendly.com",
      };
    }

    // Check if it has a valid path (not just the homepage)
    if (urlObj.pathname === "/" || urlObj.pathname === "") {
      return {
        isValid: false,
        error:
          "Please provide a specific Calendly scheduling link, not the homepage",
      };
    }

    // Basic validation for Calendly URL format
    // Should be like: https://calendly.com/username or https://calendly.com/username/meeting-type
    const pathParts = urlObj.pathname
      .split("/")
      .filter((part) => part.length > 0);
    if (pathParts.length < 1) {
      return {
        isValid: false,
        error: "Invalid Calendly URL format",
      };
    }

    return { isValid: true, error: "" };
  } catch {
    return {
      isValid: false,
      error: "Please enter a valid URL",
    };
  }
};

const CalendlyConfigForm = memo(function CalendlyConfigForm({
  config,
  onUpdate,
}) {
  const { setActionButton, clearActionButton } = useWidgetSettings();

  const [formData, setFormData] = useState({
    calendlyUrl: config.calendlyUrl || "",
    hideEventTypeDetails: config.hideEventTypeDetails || false,
    hideLandingPageDetails: config.hideLandingPageDetails || false,
  });

  const [urlError, setUrlError] = useState("");
  const [urlValidated, setUrlValidated] = useState(false);

  // Add handler to stop propagation
  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation();
    }
  }, []);

  const handleUrlChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, calendlyUrl: value }));

    if (!value.trim()) {
      setUrlError("");
      setUrlValidated(false);
      return;
    }

    const validation = validateCalendlyUrl(value);
    if (validation.isValid) {
      setUrlError("");
      setUrlValidated(true);
    } else {
      setUrlError(validation.error);
      setUrlValidated(false);
    }
  }, []);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Validate URL if provided
      if (formData.calendlyUrl) {
        const validation = validateCalendlyUrl(formData.calendlyUrl);
        if (!validation.isValid) {
          setUrlError(validation.error);
          return;
        }
      }

      // Update the widget config
      onUpdate(formData);
    },
    [formData, onUpdate]
  );

  // Set up action button
  useEffect(() => {
    const actionButton = (
      <button
        type="button"
        onClick={handleSubmit}
        className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4" />
        Save Calendly Widget
      </button>
    );

    setActionButton(actionButton);

    return () => {
      clearActionButton();
    };
  }, [setActionButton, clearActionButton, handleSubmit]);

  const extractUsernameFromUrl = () => {
    if (!formData.calendlyUrl || !urlValidated) return null;

    try {
      const url = new URL(formData.calendlyUrl);
      const pathParts = url.pathname
        .split("/")
        .filter((part) => part.length > 0);
      return pathParts[0] || null;
    } catch {
      return null;
    }
  };

  const username = extractUsernameFromUrl();

  return (
    <div
      className="space-y-6"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      {/* Header */}
      <div className="flex items-center gap-3 pb-2 border-b border-white/10">
        <FontAwesomeIcon
          icon={faCalendarAlt}
          className="w-6 h-6 text-blue-500"
        />
        <h3 className="text-lg font-semibold text-white">
          Calendly Configuration
        </h3>
      </div>

      {/* Calendly URL Input */}
      <div className="space-y-3">
        <Label className="text-white font-medium">
          Calendly Scheduling URL
        </Label>
        <Input
          value={formData.calendlyUrl}
          onChange={(e) => {
            stopPropagation(e);
            handleUrlChange(e.target.value);
          }}
          placeholder="https://calendly.com/your-username/meeting"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/20 break-all whitespace-normal"
          style={{
            wordBreak: "break-all",
            whiteSpace: "normal",
            lineHeight: "1.4",
            minHeight: "2.5rem",
            height: "auto",
            padding: "0.75rem",
          }}
        />

        {urlError && (
          <p className="text-sm text-red-400 flex items-center gap-2">
            <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
            {urlError}
          </p>
        )}

        {urlValidated && username && (
          <div className="text-sm text-green-400 flex items-center gap-2">
            <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
            <span>Valid Calendly link for @{username}</span>
          </div>
        )}

        <div className="text-xs text-white/60 bg-white/5 p-3 rounded-lg">
          <p className="font-medium mb-2">How to get your Calendly URL:</p>
          <ul className="space-y-1">
            <li>1. Go to your Calendly account</li>
            <li>2. Navigate to your event types</li>
            <li>3. Copy the scheduling link for any event</li>
            <li>
              4. Paste it here (e.g., https://calendly.com/yourname/30min)
            </li>
          </ul>
        </div>
      </div>

      {/* Display Options */}
      <div className="space-y-4">
        <h4 className="text-white font-medium">Display Options</h4>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.hideEventTypeDetails}
              onChange={(e) => {
                stopPropagation(e);
                handleChange("hideEventTypeDetails", e.target.checked);
              }}
              className="w-4 h-4 rounded border border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/20"
            />
            <span className="text-white text-sm">Hide event type details</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.hideLandingPageDetails}
              onChange={(e) => {
                stopPropagation(e);
                handleChange("hideLandingPageDetails", e.target.checked);
              }}
              className="w-4 h-4 rounded border border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/20"
            />
            <span className="text-white text-sm">
              Hide landing page details
            </span>
          </label>
        </div>

        <div className="text-xs text-white/60 bg-blue-500/10 border border-blue-500/20 p-3 rounded-lg">
          <p className="font-medium mb-2 text-blue-400">
            About Calendly Integration:
          </p>
          <p>
            This widget embeds your Calendly scheduling page directly into your
            space, allowing visitors to book appointments without leaving your
            page.
          </p>
        </div>
      </div>
    </div>
  );
});

CalendlyConfigForm.displayName = "CalendlyConfigForm";

export default CalendlyConfigForm;
